// *************************************************************
// DataDog Shared Library (Shared between KaiWeb and KaiMobile)
// *************************************************************
import { formattedInspect, isPlainObject } from '@art-suite/art-core-ts';

export type Cleanup = () => void;
export type LogLevel = 'error' | 'warn' | 'info';
export type LogSource = 'console' | 'unhandledException' | 'unhandledRejection';
export type SendToDataDogFunction = (level: LogLevel, source: LogSource, params: unknown[]) => void;
export type BindSendToDataDogFunction = (sendToDataDog: SendToDataDogFunction) => Cleanup;

// *************************************************************
// Context Helpers
// *************************************************************

let logContext: Record<string, unknown> = {};

export const setDatadogContext = (context: Record<string, unknown>) => {
  if (context.data) {
    console.warn(
      "setDatadogContext: data property is not allowed in the context object. It'll get overwritten with per-log data.",
    );
  }
  logContext = { ...context };
};

export const getDatadogContext = () => logContext;

// *************************************************************
// Console Utils
// *************************************************************

const bindConsoleLogger = (logLevel: LogLevel, handler: (...args: any[]) => void) => {
  const originalHandler = console[logLevel];
  console[logLevel] = handler;
  return () => {
    console[logLevel] = originalHandler;
  };
};

const getCurrentBoundConsoleLogger = (logLevel: LogLevel) => console[logLevel].bind(console);

// *************************************************************
// Extracting error details from console arguments
// *************************************************************

/**
 * Extracts structured error information from console log arguments.
 *
 * Extracts up to three argument types:
 * - An Error object, used for its stack trace (falls back to a synthetic Error)
 * - A plain object, merged into the log context as structured metadata
 * - Remaining arguments, stringified as the log message
 */
export const extractConsoleArgs = (args: unknown[]) => {
  const error = args.find(arg => arg instanceof Error);
  const contextArg = args.find(isPlainObject);
  const otherArgs = args.filter(
    arg => arg !== error && arg !== contextArg,
  );

  const message = stringifyConsoleArguments(otherArgs) || error?.message || 'Unknown';

  return { error, message, contextArg };
};

export const installConsoleForwarding = (level: LogLevel, sendToDataDog: SendToDataDogFunction): Cleanup => {
  const originalHandler = getCurrentBoundConsoleLogger(level);

  let isHandlerActive = false;
  return bindConsoleLogger(level, (...args: unknown[]) => {
    originalHandler(...args);
    if (isHandlerActive) return;
    try {
      isHandlerActive = true;
      sendToDataDog(level, 'console', args);
    } finally {
      isHandlerActive = false;
    }
  });
};

// *************************************************************
// Error and String Helpers
// *************************************************************

export const normalizeUnknownError = (value: unknown): Error => value instanceof Error ? value : new Error(stringifyJsonOrString(value));

const stringifyJsonOrString = (value: unknown): string => {
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value);
  } catch {
    try {
      return formattedInspect(value);
    } catch {
      return String(value);
    }
  }
};

const stringifyConsoleArguments = (args: readonly unknown[]) => args.map(stringifyConsoleArgument).join(' ');

const stringifyConsoleArgument = (value: unknown) =>
  value instanceof Error
    ? getStacktraceString(value)
    : stringifyJsonOrString(value);

const getStacktraceString = (error?: Error) => error?.stack || error?.message;

export const getDdLogProperties = (level: LogLevel, source: LogSource, error: Error | undefined, contextArg?: Record<string, unknown>) => {
  const stackError = error ?? (level !== 'info' ? new Error('stack capture') : undefined);
  return {
    ...logContext,
    data: contextArg,
    eventPath: `${source}/${level}`,
    'error.stack': getStacktraceString(stackError),
    'error.kind': stackError?.name,
  };
};

// *************************************************************
// Bind Send to DataDog Function
// *************************************************************
/**
 * Installs error forwarding to Datadog Logs.
 *
 * Forwards unhandled exceptions, unhandled rejections, console error logs,
 * and console.warn calls.
 * Returns a cleanup function that restores the original handlers.
 */
export const createInstallDatadogErrorForwardingFunction = (
  installUnhandledExceptionForwarding: BindSendToDataDogFunction,
  installUnhandledRejectionForwarding: BindSendToDataDogFunction,
  sendToDataDog: SendToDataDogFunction,
): () => Cleanup =>
() => {
  const stopUnhandledExceptionForwarding = installUnhandledExceptionForwarding(sendToDataDog);
  const stopUnhandledRejectionForwarding = installUnhandledRejectionForwarding(sendToDataDog);
  const stopConsoleErrorForwarding = installConsoleForwarding(
    'error',
    sendToDataDog,
  );
  const stopConsoleWarnForwarding = installConsoleForwarding(
    'warn',
    sendToDataDog,
  );
  const stopConsoleInfoForwarding = installConsoleForwarding(
    'info',
    sendToDataDog,
  );
  console.log(
    'installDatadogErrorForwarding: Forwarding console error/warn/info logs, unhandled exceptions, and unhandled rejections to DataDog',
  );

  return () => {
    stopUnhandledExceptionForwarding();
    stopUnhandledRejectionForwarding();
    stopConsoleErrorForwarding();
    stopConsoleWarnForwarding();
    stopConsoleInfoForwarding();
  };
};
